/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/* eslint-disable max-len */

import { AviPermissionResource } from 'generated-types';
import * as UIPermissions from 'ng/modules/core/constants/ui-permissions.constants';
import * as StateNames from 'state-names';

/**
 * For states where access isn't limited to a permission, an object of properties along with the
 * permission can be defined to check against. For example, the Tenant Settings state checks the
 * restrictAdminTenant and restrictAllTenants properties as well.
 */
export interface IStatePermissionData {
    permission: string;
    restrictAdminTenant?: boolean;
    restrictAllTenants?: boolean;
}

/**
 * A set of properties needed for rendering navigation links.
 */
export interface IState {
    name: string;
    children?: IState[];
    permission?: AviPermissionResource | string | IStatePermissionData;
}

/**
 * List of states in the entire application. This should be treated as a source of truth for what
 * states are navigable in the app.
 * TODO: Add state-access constants here for a single source of truth (AV-209033).
 */
export const stateTree: IState[] = [
    {
        name: StateNames.AUTHENTICATED,
        children: [
            {
                name: StateNames.APPLICATIONS,
                permission: UIPermissions.UI_APPLICATION,
                children: [
                    {
                        name: StateNames.APPLICATIONS_DASHBOARD,
                    },
                    {
                        name: StateNames.APPLICATIONS_VS,
                        permission: AviPermissionResource.PERMISSION_VIRTUALSERVICE,
                        children: [
                            {
                                name: StateNames.VS_VS_LIST,
                                permission: AviPermissionResource.PERMISSION_VIRTUALSERVICE,
                            },
                            {
                                name: StateNames.VS_VS_DETAIL,
                                children: [
                                    {
                                        name: StateNames.VS_DETAIL_TOPOLOGY,
                                    },
                                    {
                                        name: StateNames.VS_DETAIL_ANALYTICS,
                                    },
                                    {
                                        name: StateNames.VS_DETAIL_VS_LOGS,
                                    },
                                    {
                                        name: StateNames.VS_DETAIL_HS_INSIGHTS,
                                    },
                                    {
                                        name: StateNames.VS_DETAIL_SECURITY,
                                        children: [
                                            {
                                                name: StateNames.VS_DETAIL_SECURITY_SECURITY_DASHBOARD,
                                            },
                                            {
                                                name: StateNames.VS_DETAIL_SECURITY_LEGACY_SECURITY,
                                            },
                                        ],
                                    },
                                    {
                                        name: StateNames.VS_DETAIL_INSIGHTS,
                                    },
                                    {
                                        name: StateNames.VS_DETAIL_EVENTS,
                                    },
                                    {
                                        name: StateNames.VS_DETAIL_ALERTS,
                                    },
                                    {
                                        name: StateNames.VS_DETAIL_DNS_RECORDS,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        name: StateNames.APPLICATIONS_VS_VIP,
                        permission: AviPermissionResource.PERMISSION_VIRTUALSERVICE,
                    },
                    {
                        name: StateNames.APPLICATIONS_POOL,
                        permission: AviPermissionResource.PERMISSION_POOL,
                        children: [
                            {
                                name: StateNames.POOL_POOL_LIST,
                                permission: AviPermissionResource.PERMISSION_POOL,
                            },
                            {
                                name: StateNames.POOL_POOL_DETAIL,
                                children: [
                                    {
                                        name: StateNames.POOL_DETAIL_ANALYTICS,
                                    },
                                    {
                                        name: StateNames.POOL_DETAIL_POOL_LOGS,
                                    },
                                    {
                                        name: StateNames.POOL_DETAIL_HS_INSIGHTS,
                                        children: [
                                            {
                                                name: StateNames.POOL_HS_INSIGHTS_DEFAULT,
                                            },
                                        ],
                                    },
                                    {
                                        name: StateNames.POOL_DETAIL_SERVER,
                                        children: [
                                            {
                                                name: StateNames.POOL_SERVER_LIST,
                                            },
                                            {
                                                name: StateNames.POOL_SERVER_DETAIL,
                                                children: [
                                                    {
                                                        name: StateNames.POOL_SERVER_DETAIL_ANALYTICS,
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        name: StateNames.POOL_DETAIL_EVENTS,
                                    },
                                    {
                                        name: StateNames.POOL_DETAIL_ALERTS,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        name: StateNames.APPLICATIONS_POOL_GROUP,
                        permission: AviPermissionResource.PERMISSION_POOLGROUP,
                    },
                    {
                        name: StateNames.APPLICATIONS_GSLBSERVICE,
                        permission: AviPermissionResource.PERMISSION_GSLBSERVICE,
                        children: [
                            {
                                name: StateNames.GSLBSERVICE_LIST,
                                permission: AviPermissionResource.PERMISSION_GSLBSERVICE,
                            },
                            {
                                name: StateNames.GSLBSERVICE_GSLBSERVICE_DETAIL,
                                permission: AviPermissionResource.PERMISSION_GSLBSERVICE,
                                children: [
                                    {
                                        name: StateNames.GSLBSERVICE_DETAIL_TOPOLOGY,
                                    },
                                    {
                                        name: StateNames.GSLBSERVICE_DETAIL_MEMBERS,
                                    },
                                    {
                                        name: StateNames.GSLBSERVICE_DETAIL_FQDN_LIST,
                                        permission: AviPermissionResource.PERMISSION_GSLB,
                                    },
                                    {
                                        name: StateNames.GSLBSERVICE_DETAIL_EVENTS,
                                        permission: AviPermissionResource.PERMISSION_VIRTUALSERVICE,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                name: StateNames.OPERATIONS,
                permission: UIPermissions.UI_OPERATIONS,
                children: [
                    {
                        name: StateNames.OPERATIONS_ALERTS,
                        permission: UIPermissions.UI_OPERATIONS_ALERTS,
                        children: [
                            {
                                name: StateNames.ALERTS_ALL_ALERTS,
                                permission: AviPermissionResource.PERMISSION_ALERT,
                            },
                            {
                                name: StateNames.ALERTS_ALERT_CONFIG,
                                permission: AviPermissionResource.PERMISSION_ALERTCONFIG,
                            },
                            {
                                name: StateNames.ALERTS_ALERT_ACTIONS,
                                permission: AviPermissionResource.PERMISSION_ALERT,
                            },
                        ],
                    },
                    {
                        name: StateNames.OPERATIONS_EVENTS,
                        permission: AviPermissionResource.PERMISSION_VIRTUALSERVICE,
                        children: [
                            {
                                name: StateNames.EVENTS_EVENTS_LIST,
                            },
                            {
                                name: StateNames.EVENTS_CONFIGURATION,
                            },
                        ],
                    },
                    {
                        name: StateNames.OPERATIONS_SERVICES,
                        permission: UIPermissions.UI_OPERATIONS_SERVICES,
                        children: [
                            {
                                name: StateNames.SERVICES_SYSLOG_LIST,
                                permission: AviPermissionResource.PERMISSION_ALERTSYSLOGCONFIG,
                            },
                            {
                                name: StateNames.SERVICES_EMAIL_LIST,
                                permission: AviPermissionResource.PERMISSION_ALERTEMAILCONFIG,
                            },
                            {
                                name: StateNames.SERVICES_SNMP_LIST,
                                permission: AviPermissionResource.PERMISSION_SNMPTRAPPROFILE,
                            },
                        ],
                    },
                    {
                        name: StateNames.OPERATIONS_TRAFFIC_CAPTURE,
                        permission: AviPermissionResource.PERMISSION_TRAFFIC_CAPTURE,
                    },
                    {
                        name: StateNames.OPERATIONS_REPORTS,
                        permission: AviPermissionResource.PERMISSION_REPORT,
                    },
                ],
            },
            {
                name: StateNames.TEMPLATES,
                permission: UIPermissions.UI_TEMPLATE,
                children: [
                    {
                        name: StateNames.TEMPLATES_PROFILES,
                        permission: UIPermissions.UI_PROFILE,
                        children: [
                            {
                                name: StateNames.PROFILES_APPLICATION_PROFILE,
                                permission: AviPermissionResource.PERMISSION_APPLICATIONPROFILE,
                            },
                            {
                                name: StateNames.PROFILES_NETWORK_PROFILE,
                                permission: AviPermissionResource.PERMISSION_NETWORKPROFILE,
                            },
                            {
                                name: StateNames.PROFILES_PERSISTENCE_PROFILE,
                                permission: AviPermissionResource.PERMISSION_APPLICATIONPERSISTENCEPROFILE,
                            },
                            {
                                name: StateNames.PROFILES_HEALTH_MONITOR,
                                permission: AviPermissionResource.PERMISSION_HEALTHMONITOR,
                            },
                            {
                                name: StateNames.PROFILES_ANALYTICS_PROFILE,
                                permission: AviPermissionResource.PERMISSION_ANALYTICSPROFILE,
                            },
                            {
                                name: StateNames.PROFILES_IPAM_PROFILE,
                                permission: AviPermissionResource.PERMISSION_IPAMDNSPROVIDERPROFILE,
                            },
                            {
                                name: StateNames.PROFILES_CUSTOM_IPAM_PROFILE,
                                permission: AviPermissionResource.PERMISSION_CUSTOMIPAMDNSPROFILE,
                            },
                            {
                                name: StateNames.PROFILES_TRAFFIC_CLONE_PROFILE,
                                permission: AviPermissionResource.PERMISSION_TRAFFICCLONEPROFILE,
                            },
                            {
                                name: StateNames.PROFILES_ICAP_PROFILE,
                                permission: AviPermissionResource.PERMISSION_ICAP,
                            },
                        ],
                    },
                    {
                        name: StateNames.TEMPLATES_POLICY,
                        permission: UIPermissions.UI_POLICIES,
                        children: [
                            {
                                name: StateNames.POLICY_NAT_POLICY,
                                permission: AviPermissionResource.PERMISSION_NATPOLICY,
                            },
                            {
                                name: StateNames.POLICY_L4_POLICY,
                                permission: AviPermissionResource.PERMISSION_L4POLICYSET,
                            },
                            {
                                name: StateNames.POLICY_DNS_POLICY,
                                permission: AviPermissionResource.PERMISSION_DNSPOLICY,
                            },
                            {
                                name: StateNames.POLICY_NETWORK_SECURITY_POLICY,
                                permission: AviPermissionResource.PERMISSION_NETWORKSECURITYPOLICY,
                            },
                            {
                                name: StateNames.POLICY_HTTP_POLICY_SET,
                                permission: AviPermissionResource.PERMISSION_HTTPPOLICYSET,
                            },
                            {
                                name: StateNames.POLICY_APPLICATION_INSIGHTS_POLICY,
                                permission: AviPermissionResource.PERMISSION_APPLICATIONINSIGHTSPOLICY,
                            },
                            {
                                name: StateNames.POLICY_POSITIVE_SECURITY_POLICY,
                                permission: AviPermissionResource.PERMISSION_POSITIVESECURITYPOLICY,
                            },
                        ],
                    },
                    {
                        name: StateNames.TEMPLATES_GROUP,
                        permission: UIPermissions.UI_GROUPS,
                        children: [
                            {
                                name: StateNames.GROUP_IP_ADDR_GROUP,
                                permission: AviPermissionResource.PERMISSION_IPADDRGROUP,
                            },
                            {
                                name: StateNames.GROUP_STRING_GROUP,
                                permission: AviPermissionResource.PERMISSION_STRINGGROUP,
                            },
                        ],
                    },
                    {
                        name: StateNames.TEMPLATES_SSL,
                        permission: UIPermissions.UI_SECURITY,
                        children: [
                            {
                                name: StateNames.SSL_CERTIFICATE,
                                permission: AviPermissionResource.PERMISSION_SSLKEYANDCERTIFICATE,
                            },
                            {
                                name: StateNames.SSL_SSL_PROFILE,
                                permission: AviPermissionResource.PERMISSION_SSLPROFILE,
                            },
                            {
                                name: StateNames.SSL_PKI_PROFILE,
                                permission: AviPermissionResource.PERMISSION_PKIPROFILE,
                            },
                            {
                                name: StateNames.SSL_AUTH_PROFILE,
                                permission: AviPermissionResource.PERMISSION_AUTHPROFILE,
                            },
                            {
                                name: StateNames.SSL_AUTH_MAPPING_PROFILE,
                                permission: AviPermissionResource.PERMISSION_AUTHMAPPINGPROFILE,
                            },
                            {
                                name: StateNames.SSL_JWT_SERVER_PROFILE,
                                permission: AviPermissionResource.PERMISSION_JWTSERVERPROFILE,
                            },
                            {
                                name: StateNames.SSL_CERTIFICATE_MANAGEMENT,
                                permission: AviPermissionResource.PERMISSION_CERTIFICATEMANAGEMENTPROFILE,
                            },
                            {
                                name: StateNames.SSL_IP_REPUTATION,
                                permission: AviPermissionResource.PERMISSION_IPREPUTATIONDB,
                            },
                            {
                                name: StateNames.SSL_GEO_DB,
                                permission: AviPermissionResource.PERMISSION_GEODB,
                            },
                            {
                                name: StateNames.SSL_HSM_GROUP,
                                permission: AviPermissionResource.PERMISSION_SSLKEYANDCERTIFICATE,
                            },
                            {
                                name: StateNames.SSL_SSO_POLICY,
                                permission: AviPermissionResource.PERMISSION_SSOPOLICY,
                            },
                            {
                                name: StateNames.SSL_CSRF_POLICY,
                                permission: AviPermissionResource.PERMISSION_CSRFPOLICY,
                            },
                            {
                                name: StateNames.SSL_TRUSTED_HOST_PROFILE,
                                permission: AviPermissionResource.PERMISSION_SYSTEMCONFIGURATION,
                            },
                        ],
                    },
                    {
                        name: StateNames.TEMPLATES_SCRIPTS,
                        permission: UIPermissions.UI_SCRIPTS,
                        children: [
                            {
                                name: StateNames.SCRIPTS_DATASCRIPT_SET,
                                permission: AviPermissionResource.PERMISSION_VSDATASCRIPTSET,
                            },
                            {
                                name: StateNames.SCRIPTS_CONTROLSCRIPTS_SET,
                                permission: AviPermissionResource.PERMISSION_VSDATASCRIPTSET,
                            },
                            {
                                name: StateNames.SCRIPTS_PROTOCOL_PARSER_SCRIPTS,
                                permission: AviPermissionResource.PERMISSION_PROTOCOLPARSER,
                            },
                        ],
                    },
                    {
                        name: StateNames.TEMPLATES_AUTOSCALE,
                        permission: AviPermissionResource.PERMISSION_AUTOSCALE,
                        children: [
                            {
                                name: StateNames.AUTOSCALE_SERVER_AUTOSCALE,
                            },
                            {
                                name: StateNames.AUTOSCALE_LAUNCH_CONFIG,
                            },
                            {
                                name: StateNames.AUTOSCALE_WEBHOOK,
                            },
                            {
                                name: StateNames.AUTOSCALE_POOL_GROUP_DEPLOYMENT_POLICY,
                            },
                        ],
                    },
                    {
                        name: StateNames.TEMPLATES_WAF,
                        permission: UIPermissions.UI_WAF,
                        children: [
                            {
                                name: StateNames.WAF_WAF_POLICY,
                                permission: AviPermissionResource.PERMISSION_WAFPOLICY,
                            },
                            {
                                name: StateNames.WAF_WAF_PROFILE,
                                permission: AviPermissionResource.PERMISSION_WAFPROFILE,
                            },
                            {
                                name: StateNames.WAF_APPLICATION_RULES,
                                permission: AviPermissionResource.PERMISSION_WAFPOLICY,
                            },
                            {
                                name: StateNames.WAF_PSM,
                                permission: AviPermissionResource.PERMISSION_WAFPOLICYPSMGROUP,
                            },
                            {
                                name: StateNames.WAF_WAF_CRS,
                                permission: AviPermissionResource.PERMISSION_WAFCRS,
                            },
                        ],
                    },
                    {
                        name: StateNames.TEMPLATES_BOT,
                        permission: AviPermissionResource.PERMISSION_BOTDETECTIONPOLICY,
                        children: [
                            {
                                name: StateNames.BOT_BOT_MANAGEMENT_POLICY,
                            },
                            {
                                name: StateNames.BOT_BOT_MAPPING,
                            },
                        ],
                    },
                    {
                        name: StateNames.TEMPLATES_ERROR_PAGE,
                        permission: UIPermissions.UI_ERRORPAGE,
                        children: [
                            {
                                name: StateNames.ERROR_PAGE_ERROR_PAGE_PROFILE,
                                permission: AviPermissionResource.PERMISSION_ERRORPAGEPROFILE,
                            },
                            {
                                name: StateNames.ERROR_PAGE_ERROR_PAGE_BODY,
                                permission: AviPermissionResource.PERMISSION_ERRORPAGEBODY,
                            },
                        ],
                    },
                    {
                        name: StateNames.TEMPLATES_FILE_OBJECTS,
                        permission: AviPermissionResource.PERMISSION_FILE_OBJECT,
                    },
                ],
            },
            {
                name: StateNames.INFRASTRUCTURE,
                permission: UIPermissions.UI_INFRASTRUCTURE,
                children: [
                    {
                        name: StateNames.INFRASTRUCTURE_DASHBOARD,
                        permission: AviPermissionResource.PERMISSION_SERVICEENGINE,
                    },
                    {
                        name: StateNames.INFRASTRUCTURE_GET_STARTED,
                        permission: AviPermissionResource.PERMISSION_CLOUD,
                    },
                    {
                        name: StateNames.INFRASTRUCTURE_CLOUD,
                        permission: AviPermissionResource.PERMISSION_CLOUD,
                    },
                    {
                        name: StateNames.INFRASTRUCTURE_CLOUD_RESOURCES,
                        permission: UIPermissions.UI_INFRASTRUCTURE_DETAIL,
                        children: [
                            {
                                name: StateNames.CLOUD_RESOURCES_SERVICE_ENGINE,
                                permission: AviPermissionResource.PERMISSION_SERVICEENGINE,
                                children: [
                                    {
                                        name: StateNames.SERVICE_ENGINE_SERVICE_ENGINE_DETAIL,
                                        permission: AviPermissionResource.PERMISSION_SERVICEENGINE,
                                        children: [
                                            {
                                                name: StateNames.SERVICE_ENGINE_DETAIL_SUMMARY,
                                            },
                                            {
                                                name: StateNames.SERVICE_ENGINE_DETAIL_HS_INSIGHTS,
                                                children: [
                                                    {
                                                        name: StateNames.SERVICE_ENGINE_HS_INSIGHTS_DEFAULT,
                                                    },
                                                ],
                                            },
                                            {
                                                name: StateNames.SERVICE_ENGINE_DETAIL_EVENTS,
                                            },
                                            {
                                                name: StateNames.SERVICE_ENGINE_DETAIL_ALERTS,
                                            },
                                            {
                                                name: StateNames.SERVICE_ENGINE_DETAIL_TRAFFIC_CAPTURE,
                                                permission: AviPermissionResource.PERMISSION_TRAFFIC_CAPTURE,
                                                children: [
                                                    {
                                                        name: StateNames.SERVICE_ENGINE_TRAFFIC_CAPTURE_DEFAULT,
                                                    },
                                                ],
                                            },
                                            {
                                                name: StateNames.SERVICE_ENGINE_DETAIL_BGP_PEERS,
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                name: StateNames.CLOUD_RESOURCES_SEGROUP,
                                permission: AviPermissionResource.PERMISSION_SERVICEENGINEGROUP,
                            },
                            {
                                name: StateNames.CLOUD_RESOURCES_NETWORK,
                                permission: AviPermissionResource.PERMISSION_NETWORK,
                            },
                            {
                                name: StateNames.CLOUD_RESOURCES_VRF_CONTEXT,
                                permission: AviPermissionResource.PERMISSION_VRFCONTEXT,
                            },
                            {
                                name: StateNames.CLOUD_RESOURECES_AVAILABILITY_ZONE,
                            },
                        ],
                    },
                    {
                        name: StateNames.INFRASTRUCTURE_GSLB,
                        permission: UIPermissions.UI_INFRASTRUCTURE_GSLB,
                        children: [
                            {
                                name: StateNames.GSLB_SITES,
                                permission: AviPermissionResource.PERMISSION_GSLB,
                            },
                            {
                                name: StateNames.GSLB_SUBDOMAINS,
                                permission: AviPermissionResource.PERMISSION_GSLB,
                            },
                            {
                                name: StateNames.GSLB_GEO_PROFILE,
                                permission: AviPermissionResource.PERMISSION_GSLBGEODBPROFILE,
                            },
                            {
                                name: StateNames.GSLB_LEADER_CHANGE,
                                permission: AviPermissionResource.PERMISSION_GSLB,
                            },
                        ],
                    },
                ],
            },
            {
                name: StateNames.ADMINISTRATION,
                permission: UIPermissions.UI_ADMINISTRATION,
                children: [
                    {
                        name: StateNames.ADMINISTRATION_DASHBOARD,
                        permission: AviPermissionResource.PERMISSION_CONTROLLER,
                    },
                    {
                        name: StateNames.ADMINISTRATION_USERS,
                        permission: UIPermissions.UI_ACCOUNT,
                        children: [
                            {
                                name: StateNames.USERS_USER_LIST,
                                permission: AviPermissionResource.PERMISSION_USER,
                            },
                            {
                                name: StateNames.USERS_USER_PROFILE_LIST,
                                permission: AviPermissionResource.PERMISSION_USER,
                            },
                            {
                                name: StateNames.USERS_ROLE_LIST,
                                permission: AviPermissionResource.PERMISSION_ROLE,
                            },
                            {
                                name: StateNames.USERS_TENANT_LIST,
                                permission: AviPermissionResource.PERMISSION_TENANT,
                            },
                            {
                                name: StateNames.USERS_TENANT_SETTINGS,
                                permission: {
                                    permission: AviPermissionResource.PERMISSION_TENANT_SYSTEM_CONFIGURATION,
                                    restrictAdminTenant: true,
                                    restrictAllTenants: true,
                                },
                            },
                        ],
                    },
                    {
                        name: StateNames.ADMINISTRATION_SYSTEM_SETTINGS,
                        permission: AviPermissionResource.PERMISSION_SYSTEMCONFIGURATION,
                    },
                    {
                        name: StateNames.ADMINISTRATION_EXTERNAL_SERVICES,
                        permission: UIPermissions.UI_EXTERNAL_SERVICES,
                        children: [
                            {
                                name: StateNames.EXTERNAL_CLOUD_SERVICES,
                                permission: AviPermissionResource.PERMISSION_ALBSERVICESSTATUS,
                            },
                            {
                                name: StateNames.EXTERNAL_CUSTOMER_PROGRAM,
                                permission: AviPermissionResource.PERMISSION_TELEMETRY_API,
                            },
                        ],
                    },
                    {
                        name: StateNames.ADMINISTRATION_LICENSING,
                        permission: AviPermissionResource.PERMISSION_SYSTEMCONFIGURATION,
                    },
                    {
                        name: StateNames.ADMINISTRATION_CONTROLLER_DETAIL,
                        permission: UIPermissions.UI_CONTROLLER,
                        children: [
                            {
                                name: StateNames.CONTROLLER_DETAIL_CLUSTER,
                                permission: AviPermissionResource.PERMISSION_CONTROLLER,
                            },
                            {
                                name: StateNames.CONTROLLER_DETAIL_SOFTWARE,
                                permission: AviPermissionResource.PERMISSION_IMAGE,
                            },
                            {
                                name: StateNames.CONTROLLER_DETAIL_UPDATE,
                                permission: AviPermissionResource.PERMISSION_UPGRADE_OPS,
                                children: [
                                    {
                                        name: StateNames.CONTROLLER_DETAIL_UPDATE_SYSTEM,
                                    },
                                    {
                                        name: StateNames.CONTROLLER_DETAIL_UPDATE_SEG,
                                    },
                                ],
                            },
                            {
                                name: StateNames.CONTROLLER_DETAIL_UPLOAD_HSM_PACKAGES,
                                permission: AviPermissionResource.PERMISSION_CONTROLLER,
                            },
                            {
                                name: StateNames.CONTROLLER_DETAIL_BACKUP,
                                permission: AviPermissionResource.PERMISSION_CONTROLLER,
                            },
                            {
                                name: StateNames.CONTROLLER_DETAIL_REPORTS,
                                permission: AviPermissionResource.PERMISSION_SYSTEMREPORT,
                                children: [
                                    {
                                        name: StateNames.REPORTS_DETAILS,
                                        children: [
                                            {
                                                name: StateNames.REPORTS_DETAILS_SYSTEM_READINESS,
                                            },
                                            {
                                                name: StateNames.REPORTS_DETAILS_DRY_RUN_REPORT,
                                            },
                                            {
                                                name: StateNames.REPORTS_DETAILS_SYSTEM_TASK,
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        name: StateNames.ADMINISTRATION_USER_CREDENTIALS,
                        permission: AviPermissionResource.PERMISSION_USER_CREDENTIAL,
                    },
                    {
                        name: StateNames.ADMINISTRATION_SUPPORT,
                        permission: UIPermissions.UI_SUPPORT,
                        children: [
                            {
                                name: StateNames.SUPPORT_TECH_SUPPORT,
                                permission: AviPermissionResource.PERMISSION_TECHSUPPORT,
                                children: [
                                    {
                                        name: StateNames.SUPPORT_TECH_SUPPORT_DETAIL,
                                        children: [
                                            {
                                                name: StateNames.SUPPORT_TECH_SUPPORT_DETAILS_READINESS,
                                            },
                                            {
                                                name: StateNames.SUPPORT_TECH_SUPPORT_DETAILS_TASK,
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                name: StateNames.SUPPORT_CRASH_REPORTS,
                                permission: AviPermissionResource.PERMISSION_CONTROLLER,
                            },
                        ],
                    },
                ],
            },
        ],
    },
];
